import { useExtensionData } from '$extensionSrc/hooks/extensionStorageListener'
import {
  USER_FEATURE_FLAG,
  UserFeatureFlagValue,
  UserFeatureFlags,
} from '$extensionSrc/personalization/flags'
import { STORAGE_KEY } from '$extensionSrc/utils/enums'
import * as localStorage from '$extensionSrc/utils/localStorage'
import { _debug } from '$extensionSrc/utils/logging'

/**
 * Get the entire user feature flags object from local storage
 * @returns The user feature flags object, or null if not present
 */
export async function getUserFeatureFlags(): Promise<UserFeatureFlags | null> {
  return (await localStorage.getSingle(
    STORAGE_KEY.USER_FEATURES,
  )) as UserFeatureFlags | null
}

/**
 * Get the value of a specific feature flag
 * Convenience function for handling null/undefined cases
 * @param flagName The feature flag to retrieve
 * @param flags The user feature flags to retrieve the flag from
 * @returns The flag value, or null if not set or flags not present
 */
export function getFlagFromUserFeatureFlags(
  flagName: USER_FEATURE_FLAG,
  flags: UserFeatureFlags | null,
): UserFeatureFlagValue | null {
  if (!flags) {
    return null
  }

  return flags[flagName] || null
}

/**
 * Get the value of a specific feature flag
 * @param flagName The feature flag to retrieve
 * @returns The flag value, or null if not set or storage is empty
 */
export async function getFlagFromStorage(
  flagName: USER_FEATURE_FLAG,
): Promise<UserFeatureFlagValue | null> {
  try {
    const flags = await getUserFeatureFlags()
    return getFlagFromUserFeatureFlags(flagName, flags)
  } catch (error) {
    _debug(
      `Error getting feature flag ${flagName}: ${error}`,
      'featureFlags',
      'e',
    )
    return null
  }
}

export function useUserFeatureFlags(): UserFeatureFlags | null | undefined {
  const { data, isLoaded } = useExtensionData({
    local: [STORAGE_KEY.USER_FEATURES],
  })

  if (!isLoaded || !data) {
    return undefined
  }

  return data[STORAGE_KEY.USER_FEATURES] || null
}
